import { useEffect, useState } from "react";

import { fixZeroLengthStrokes } from "utils/pdfZeroLengthStrokes";

/**
 * Returns the PDF to hand to react-pdf for a blob URL. pdf.js skips zero-length stroked
 * lines (used for the data points in Abbott rate charts), so the bytes are patched first.
 * Falls back to the original URL if anything goes wrong. The stored blob URL is never
 * replaced, so downloads, printing and page insertion keep the untouched file.
 */
const useDisplayPdf = (blobUrl) => {
  // { source, file, revoked } of the latest prepared copy; `revoked` is set by the
  // effect cleanup so a revoked object URL is never handed out again.
  const [display, setDisplay] = useState(null);

  useEffect(() => {
    if (!blobUrl) return undefined;

    const entry = { source: blobUrl, file: blobUrl, revoked: false };
    let fixedUrl = null;

    (async () => {
      try {
        const response = await fetch(blobUrl);
        const original = new Uint8Array(await response.arrayBuffer());
        const fixed = await fixZeroLengthStrokes(original);
        if (entry.revoked) return;

        if (fixed !== original) {
          fixedUrl = URL.createObjectURL(
            new Blob([fixed], { type: "application/pdf" })
          );
          entry.file = fixedUrl;
        }
      } catch (error) {
        console.error("Could not prepare PDF for display", error);
      }
      if (!entry.revoked) setDisplay(entry);
    })();

    return () => {
      entry.revoked = true;
      if (fixedUrl) URL.revokeObjectURL(fixedUrl);
    };
  }, [blobUrl]);

  // Until the patched copy is ready (or after the URL changed), show nothing new.
  return display && display.source === blobUrl && !display.revoked
    ? display.file
    : null;
};

export default useDisplayPdf;
